import { IconSize } from 'assets/icons/icon-sizes';
import DurationLabel from 'components/DurationLabel';
import {
    BodyLarge,
    BodyRegular,
    BodySmall,
    FlexCenter,
    FlexCol,
    FlexColMd,
    FlexColXs,
    FlexMdAlignCenter,
    Headline3,
} from 'components/Elements';
import { ActivityType, getActivityTypeIcon } from 'models/Activity';
import { Resource } from 'models/Resources';
import styled from 'styled-components';
import { theme, sizes, breakpoints } from 'theme';
import strings from '../../../strings/strings.json';
import ExternalLinkIcon from '../../../assets/icons/navigation/ExternalLinkIcon';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import { useContext } from 'react';
import ActivityTypeLabel from 'pages/build-course/components/ActivityTypeLabel';

export default function ActivityCardGrid(): JSX.Element {
    const learnerCourse = useContext(LearnerCourseContext).learnerCourse!;
    const { step, setStep } = useContext(LearnerCourseContext);

    return (
        <>
            <Headline3>{strings.activityCard.title}</Headline3>
            <CardGrid>
                {learnerCourse.modules
                    .find((module) => module.id === step.id)!
                    .activities.filter((a) => a.type !== ActivityType.PRESENTATION_SLIDE)
                    .map((activity, index) => (
                        <CardButton
                            key={index}
                            onClick={() => {
                                setStep({ id: activity.activityStatusId, stepType: 'Activity' });
                            }}
                            aria-label={`${strings.immersiveCourseViewButtons.moduleOverviewClickableActivityAria}: ${activity.title}`}
                        >
                            <IconContainer>{getActivityTypeIcon(activity, IconSize.XLARGE)}</IconContainer>
                            <CardInfo>
                                <FlexCol>
                                    {activity.type !== ActivityType.LIVE_TRAINING && (
                                        <BodySmall>{`${strings.activityCard.activity} ${activity.order}`}</BodySmall>
                                    )}
                                    <DurationContainer>
                                        <ActivityTypeLabel
                                            activityType={activity.type}
                                            buildMethod={activity.buildMethod}
                                            hideIcon
                                        />
                                        {activity.durationInMins && (
                                            <DurationLabel
                                                durationInMins={activity.durationInMins}
                                                hideTitle
                                                noBackgroundColour
                                            />
                                        )}
                                    </DurationContainer>
                                    <BodyLarge $bold>{activity.title}</BodyLarge>
                                </FlexCol>
                            </CardInfo>
                        </CardButton>
                    ))}
            </CardGrid>
        </>
    );
}

type ResourceCardGridProps = {
    resources: Resource[];
};

export function ResourceCardGrid({ resources }: ResourceCardGridProps): JSX.Element {
    return (
        <FlexColMd>
            <Headline3>{strings.additonalResources.title}</Headline3>
            <CardGrid>
                {resources.map((resource, index) => (
                    <LinkButton
                        key={index}
                        onClick={() => {
                            window.open(resource.url, '_blank');
                        }}
                        aria-label={strings.general.openInNewTab}
                    >
                        <IconContainer>
                            <ExternalLinkIcon colour={theme.textColour} size={IconSize.XLARGE} />
                        </IconContainer>
                        <FlexColXs>
                            <BodyRegular $bold>{resource.title}</BodyRegular>
                        </FlexColXs>
                    </LinkButton>
                ))}
            </CardGrid>
        </FlexColMd>
    );
}

const DurationContainer = styled(FlexMdAlignCenter)`
    justify-content: space-between;
`;

const IconContainer = styled(FlexCenter)`
    width: 34px;
    height: 34px;
    flex-shrink: 0;
`;

const CardInfo = styled(FlexColMd)`
    justify-content: space-between;
    width: 100%;
`;

const CardButton = styled.button`
    all: unset;
    display: flex;
    gap: ${sizes.spacingMd};
    padding: ${sizes.spacingMd};
    align-items: center;
    cursor: pointer;
    min-height: 135px;

    box-sizing: border-box;
    border: 2px solid #d2dadf;
    border-radius: 8px;
    box-shadow: 2px 4px 6px 0 rgba(0, 0, 0, 0.1);
    background: ${theme.learnerActivityCard.background};

    &:focus {
        background: ${theme.navigationBar.buttonFocusBackground};
        border: 2px solid ${theme.navigationBar.buttonFocusBorder};
        outline: ${theme.navigationBar.buttonFocusBorder};
    }

    &:hover {
        background: ${theme.learnerActivityCard.backgroundHover};
    }
`;

const LinkButton = styled(CardButton)`
    height: 80px;
    min-height: unset;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: ${sizes.spacingMd};
    row-gap: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.md}) {
        grid-template-columns: auto;
    }
`;
